/**
 * <s-share-button> — shares the current product page.
 * Uses the device share sheet when available; otherwise copies the link and shows a short confirmation.
 *
 * Attributes: id (command target), data-url, data-title, data-copied-label
 * Commands:   --share — open the share sheet or copy the link
 * Parts:      [data-share-label] (visible label), [data-share-status] (polite live region)
 */
class SShareButton extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command === '--share') this.#share();
    }, { signal: this.#ac.signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  async #share() {
    const url = new URL(this.dataset.url || location.pathname, location.origin).toString();
    const title = this.dataset.title || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      this.#confirm();
    } catch {
      window.prompt(title, url);
    }
  }

  #confirm() {
    const label = this.querySelector('[data-share-label]');
    const status = this.querySelector('[data-share-status]');
    const copied = this.dataset.copiedLabel || '';
    if (!label) return;
    const original = label.dataset.original || label.textContent;
    label.dataset.original = original;
    label.textContent = copied;
    if (status) status.textContent = copied;
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      label.textContent = original;
      if (status) status.textContent = '';
    }, 2000);
  }
}

if (!customElements.get('s-share-button')) customElements.define('s-share-button', SShareButton);
